import type { Cell } from './types.ts'
import type { Difficulty } from '../config/ai-difficulty.ts'
import { DIFFICULTIES } from '../config/ai-difficulty.ts'
import { greedyPlayer } from './ai-engines/greedy.ts'
import { createNoisyGreedyPlayer } from './ai-engines/noisy-greedy.ts'
import { createMinimaxPlayer } from './ai-engines/minimax.ts'
import { createNoisyMinimaxPlayer } from './ai-engines/noisy-minimax.ts'
import type { Player } from './ai-engines/types.ts'

// evalPosition は互換のため再エクスポート（旧コードやテストが src/game/ai.ts から import している可能性のため）
export { evalPosition } from './eval.ts'

// 難易度 → Player 解決。Player 実体は noisy-greedy / minimax の場合 factory で生成されるため、
// 同一 difficulty の Player をキャッシュして毎手の生成コストを抑える。
const playerCache = new Map<Difficulty, Player>()

function resolvePlayer(difficulty: Difficulty): Player {
  const cached = playerCache.get(difficulty)
  if (cached) return cached

  const cfg = DIFFICULTIES[difficulty]
  let player: Player
  switch (cfg.engine) {
    case 'noisy-greedy': {
      const noise = cfg.noiseRate ?? 0.3
      player = createNoisyGreedyPlayer(noise)
      break
    }
    case 'greedy':
      player = greedyPlayer
      break
    case 'minimax2':
      player = createMinimaxPlayer(2)
      break
    case 'minimax3':
      player = createMinimaxPlayer(3)
      break
    case 'noisy-minimax2': {
      const depth = cfg.depth ?? 2
      const noise = cfg.noiseRate ?? 0.2
      player = createNoisyMinimaxPlayer(depth, noise)
      break
    }
  }
  playerCache.set(difficulty, player)
  return player
}

// CPU の着手選択。難易度と CPU 色を受け取り、対応する Player に委譲する。
export function chooseCpuMove(
  board: Cell[][],
  difficulty: Difficulty,
  cpuColor: 'black' | 'white',
  rng: () => number = Math.random
): { row: number; col: number } | null {
  const player = resolvePlayer(difficulty)
  return player.decide(board, cpuColor, { rng })
}
